import { useEffect, useState, useSyncExternalStore } from 'react'

import { identityHeaders } from '../api/client'
import { t } from '../i18n'

/**
 * One photograph, fetched the way every other call is identified.
 *
 * An `<img src>` cannot carry a request header, exactly as `EventSource`
 * cannot: the browser issues that GET on its own and in trusted-header mode it
 * arrives with no person on it, so the node answers 401 and the reader gets a
 * broken-image glyph with nothing to explain it. The bytes are fetched here
 * with the same header the typed client sends and handed to the tag as an
 * object URL instead.
 *
 * **A missing photograph is not an absent one.** The register hands out a
 * `media_id` the moment the record replicates, but the file travels in the
 * photos module's own stream and nothing orders the two — so this node can
 * hold the row before it holds the image. The image route answers 404 while
 * the upload is still in a handheld's queue or no thumbnail has been derived
 * yet, and 503 when another box has the file. All of those are *waiting*, and
 * this draws them as waiting. Only an empty evidence list means no photograph
 * was taken, and that is the caller's to say, not this component's. Drawing
 * "no photo" here would tell a superintendent a defect has no evidence when it
 * has evidence that simply has not arrived.
 */

type Shown =
  | { kind: 'loading' }
  | { kind: 'shown'; url: string }
  /** The node has the record but not the file, yet. Worth coming back for. */
  | { kind: 'waiting' }
  /** Refused or unreachable for a reason retrying will not fix. */
  | { kind: 'failed' }

/** 404 and 503 both mean the file has not got here; see the note above. */
const NOT_HERE_YET = new Set([404, 503])

/**
 * How many thumbnails are asked for at once, across every gallery on the page.
 *
 * A register of a hundred and nineteen photographs mounts a hundred and
 * nineteen of these, and each used to ask at once. Every one of those requests
 * holds a database connection on the instance while it is answered, and the
 * instance has fifteen: measured against Postgres with the assembled app, 119
 * at once left 65 timing out after a minute and drawn as failed, while 6 at a
 * time answered all 119 in under three seconds. The rest wait their turn here,
 * in order, and leave the queue if their tile goes away first.
 *
 * Only thumbnails queue. The one full picture somebody opened is not put
 * behind a gallery's worth of tiles.
 */
const AT_ONCE = 6
let asking = 0
const queue: (() => void)[] = []

/** Wait for a turn to ask; the answer is how to give the turn back. */
function turn(signal: AbortSignal): Promise<() => void> {
  return new Promise((resolve, reject) => {
    const begin = () => {
      asking += 1
      let given = false
      resolve(() => {
        if (given) return
        given = true
        asking -= 1
        queue.shift()?.()
      })
    }
    if (asking < AT_ONCE) {
      begin()
      return
    }
    const leave = () => {
      const at = queue.indexOf(next)
      if (at >= 0) queue.splice(at, 1)
      reject(signal.reason)
    }
    const next = () => {
      signal.removeEventListener('abort', leave)
      begin()
    }
    queue.push(next)
    signal.addEventListener('abort', leave, { once: true })
  })
}

/**
 * Photographs whose thumbnail was just redone on this page, and how many times.
 *
 * A redo of a thumbnail that was missing here or would not open writes the
 * same key and the same size again, so nothing the register reads changes,
 * and a tile that drew it as waiting or failed would go on doing so. Told
 * here, every place drawing that photograph asks for it again.
 */
const redone = new Map<string, number>()
const listeners = new Set<() => void>()

function listen(changed: () => void): () => void {
  listeners.add(changed)
  return () => listeners.delete(changed)
}

/** Say this photograph's thumbnail was just made again. */
export function thumbnailRedone(mediaId: string): void {
  redone.set(mediaId, (redone.get(mediaId) ?? 0) + 1)
  for (const changed of listeners) changed()
}

export function Photograph({
  mediaId,
  caption,
  size = 'thumbnail',
  version = null,
}: {
  mediaId: string
  caption?: string | null
  size?: 'thumbnail' | 'image'
  /**
   * Which thumbnail the instance last said this photograph has. A new one,
   * after Redo thumbnail, is fetched again under its own address, so the
   * picture shown is never one the browser kept from before.
   */
  version?: string | null
}) {
  const [shown, setShown] = useState<Shown>({ kind: 'loading' })
  const fresh = useSyncExternalStore(listen, () => redone.get(mediaId) ?? 0)

  useEffect(() => {
    const abort = new AbortController()
    let objectUrl: string | null = null

    void (async () => {
      let giveBack: (() => void) | null = null
      try {
        if (size === 'thumbnail') giveBack = await turn(abort.signal)
        const which = new URLSearchParams()
        if (version) which.set('v', version)
        if (fresh) which.set('r', String(fresh))
        const asked = which.toString() ? `?${which.toString()}` : ''
        const response = await fetch(`/photos/media/${mediaId}/${size}${asked}`, {
          headers: identityHeaders(),
          credentials: 'same-origin',
          signal: abort.signal,
        })
        if (!response.ok) {
          setShown({ kind: NOT_HERE_YET.has(response.status) ? 'waiting' : 'failed' })
          return
        }
        objectUrl = URL.createObjectURL(await response.blob())
        setShown({ kind: 'shown', url: objectUrl })
      } catch {
        // An abort lands here too, on the way out of a screen. Setting state
        // after that is harmless — the component is gone — and distinguishing
        // the two would buy nothing a reader can see.
        if (!abort.signal.aborted) setShown({ kind: 'failed' })
      } finally {
        giveBack?.()
      }
    })()

    return () => {
      abort.abort()
      // The blob stays in memory for the life of the document until this is
      // called, and a gallery of site photographs is not small.
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [mediaId, size, version, fresh])

  if (shown.kind === 'shown') {
    return <img className="photo" src={shown.url} alt={caption || t('photo.alt')} />
  }
  return (
    <div
      className="photo placeholder"
      role="img"
      aria-label={shown.kind === 'waiting' ? t('photo.waiting') : t('photo.failed')}
      data-state={shown.kind}
    >
      <span>
        {shown.kind === 'loading'
          ? t('photo.loading')
          : shown.kind === 'waiting'
            ? t('photo.waiting')
            : t('photo.failed')}
      </span>
    </div>
  )
}
